import { useEffect, useState, type FormEvent } from 'react';
import { loginApi, registerApi } from './api';
import { useClientAuth } from './auth';

export type AuthDialogMode = 'login' | 'register';

interface LoginDialogProps {
  open: boolean;
  initialMode?: AuthDialogMode;
  onClose: () => void;
}

export default function LoginDialog({ open, initialMode = 'login', onClose }: LoginDialogProps) {
  const setAuth = useClientAuth((state) => state.setAuth);
  const [mode, setMode] = useState<AuthDialogMode>(initialMode);
  const [username, setUsername] = useState('');
  const [nickname, setNickname] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setError('');
  }, [open, initialMode]);

  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [open, onClose]);

  if (!open) return null;

  function switchMode(nextMode: AuthDialogMode) {
    setMode(nextMode);
    setPassword('');
    setConfirmPassword('');
    setError('');
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalizedUsername = username.trim();
    if (!normalizedUsername || !password) return;
    if (mode === 'register') {
      if (!/^[A-Za-z0-9_-]{3,32}$/.test(normalizedUsername)) {
        setError('用户名需为 3–32 位字母、数字、下划线或短横线');
        return;
      }
      if (password.length < 8) {
        setError('密码至少需要 8 个字符');
        return;
      }
      if (password !== confirmPassword) {
        setError('两次输入的密码不一致');
        return;
      }
    }

    setPending(true);
    setError('');
    try {
      const result = mode === 'login'
        ? await loginApi(normalizedUsername, password)
        : await registerApi(normalizedUsername, nickname, password);
      setAuth(result.access_token, result.refresh_token, result.user);
      setPassword('');
      setConfirmPassword('');
      onClose();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : `${mode === 'login' ? '登录' : '注册'}失败，请稍后重试`);
    } finally {
      setPending(false);
    }
  }

  const registering = mode === 'register';
  const submitDisabled = pending
    || !username.trim()
    || !password
    || (registering && (!confirmPassword || password !== confirmPassword));

  return (
    <div className="login-overlay" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="login-dialog glass" role="dialog" aria-modal="true" aria-labelledby="login-title">
        <button className="login-close" type="button" aria-label="关闭账号窗口" onClick={onClose}>×</button>
        <div className="login-brand" aria-hidden="true">🐟</div>
        <span className="login-eyebrow">科普趣答 · 鱼虾图鉴</span>
        <h2 id="login-title">{registering ? '创建你的账号' : '欢迎回来'}</h2>
        <p>{registering ? '保存你的提问和阅读进度，下次打开还能接着看。' : '登录后继续提问，你的历史对话会自动同步。'}</p>
        <div className="login-tabs" role="tablist" aria-label="账号操作">
          <button
            type="button"
            role="tab"
            aria-selected={!registering}
            className={!registering ? 'active' : ''}
            onClick={() => switchMode('login')}
          >
            登录
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={registering}
            className={registering ? 'active' : ''}
            onClick={() => switchMode('register')}
          >
            注册
          </button>
        </div>
        <form onSubmit={submit}>
          <label htmlFor="client-username">用户名</label>
          <input
            id="client-username"
            className="input"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            autoFocus
            maxLength={32}
            placeholder={registering ? '3–32 位字母、数字、_ 或 -' : '请输入用户名'}
          />
          {registering && (
            <>
              <label htmlFor="client-nickname">昵称（选填）</label>
              <input
                id="client-nickname"
                className="input"
                value={nickname}
                onChange={(event) => setNickname(event.target.value)}
                autoComplete="nickname"
                maxLength={32}
                placeholder="在页面上显示的名字"
              />
            </>
          )}
          <label htmlFor="client-password">密码</label>
          <input
            id="client-password"
            className="input"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete={registering ? 'new-password' : 'current-password'}
            maxLength={128}
            placeholder={registering ? '至少 8 个字符' : '请输入密码'}
          />
          {registering && (
            <>
              <label htmlFor="client-password-confirm">确认密码</label>
              <input
                id="client-password-confirm"
                className="input"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                autoComplete="new-password"
                maxLength={128}
                placeholder="请再次输入密码"
              />
            </>
          )}
          {error && <div className="login-error" role="alert">{error}</div>}
          <button className="btn primary login-submit" type="submit" disabled={submitDisabled}>
            {pending ? (registering ? '注册中…' : '登录中…') : (registering ? '注册并登录' : '登录')}
          </button>
        </form>
        <div className="login-trust"><span>✓ 对话自动保存</span><span>✓ 回答附资料出处</span></div>
        <small>{registering ? '已有账号？点击上方“登录”即可继续。' : '还没有账号？点击上方“注册”，只需用户名和密码。'}</small>
      </section>
    </div>
  );
}
